import { CarAxleType } from '@navio/nsdk';
import { NoddiAsyncError } from 'noddi-types';
import { NoddiIcon, NoddiIconName, cva } from 'noddi-ui-common';
import { NavioListCard, NoddiBadge, NoddiIconButton } from 'noddi-ui-web';
import { commonTexts, formatCurrencyAmount, getDimensionsLoadSpeedString, getTireBrandName } from 'noddi-util';
import type { ReactNode } from 'react';
import { TireClassificationBadge } from '../Tire/TireClassificationBadge';
import { TireEuLabelGrades } from '../Tire/TireEuLabelGrades';
import { TirePriorityBadge } from '../Tire/TirePriorityBadge';
import { TireOption, TireOptionTire } from './tireOption';

type Translations = typeof commonTexts.tireOptions;

export type TireOptionAction = {
  label: string;
  icon: NoddiIconName;
  onClick: () => void;
  /** Keeps the button visible but inert, e.g. while the options on screen are being refreshed. */
  disabled?: boolean;
  /** A toggle that is on, e.g. the star of the recommended option: drawn filled. */
  active?: boolean;
};

// The icon inherits the button's text color; an active toggle also fills it.
const actionButtonVariants = cva('', {
  variants: {
    active: {
      // The icon's shapes carry their own fill='none', so fill them, not the svg.
      true: 'text-amber-500 hover:text-amber-600 [&_svg_*]:fill-amber-400',
      false: ''
    }
  }
});

type Props<T extends TireOption> = {
  title: string;
  icon?: NoddiIconName;
  options: T[];
  /** Icon buttons at the end of each row, e.g. edit and swap. */
  getActions?: (option: T) => TireOptionAction[];
  /** Opens the option, e.g. its detail sheet, from a click anywhere on its row. */
  onOptionClick?: (option: T) => void;
  headerNode?: ReactNode;
  /** Pinned below the options, e.g. services added to whichever option the customer picks. */
  footer?: ReactNode;
  /** The options are still loading. */
  isPending?: boolean;
  /** The options failed to load; shown in the body so a failure never reads as no options. */
  error?: NoddiAsyncError | null;
  translations?: Partial<Translations>;
};

/**
 * The tire options of an offer, one numbered row each: tire, EU label grades, quantity and price.
 * Staggered options with a different tire per axle get one line per tire.
 */
export const TireOptionsCard = <T extends TireOption>({
  title,
  icon = 'Wheel',
  options,
  getActions,
  onOptionClick,
  headerNode,
  footer,
  isPending,
  error,
  translations
}: Props<T>) => {
  const texts = { ...commonTexts.tireOptions, ...translations };
  const rows = options.map((option, index) => ({ option, number: index + 1 }));

  return (
    <NavioListCard
      title={title}
      icon={icon}
      items={rows}
      getItemKey={({ option }) => option.id}
      onItemClick={onOptionClick && (({ option }) => onOptionClick(option))}
      // The rows are numbered, so a count beside the title only repeats the last number.
      hideCount
      headerNode={headerNode}
      footer={footer}
      isPending={isPending}
      error={error}
      translations={{ noItems: texts.noOptions }}
      renderItem={({ option, number }) => (
        <TireOptionRow option={option} number={number} actions={getActions?.(option) ?? []} texts={texts} />
      )}
    />
  );
};

type RowProps = {
  option: TireOption;
  number: number;
  actions: TireOptionAction[];
  texts: Translations;
};

const TireOptionRow = ({ option, number, actions, texts }: RowProps) => {
  const { tires } = option;
  const lines = getTireLines(tires);
  const totalQuantity = sumQuantity(tires);
  const frontQuantity = sumQuantity(tires.filter((tire) => tire.axleType === CarAxleType.FRONT));
  const rearQuantity = sumQuantity(tires.filter((tire) => tire.axleType === CarAxleType.REAR));
  const currency = tires[0]?.unitPrice.currency;
  const totalAmount = tires.reduce((sum, tire) => sum + tire.unitPrice.amount * tire.quantity, 0);
  const hasOneUnitPrice = new Set(tires.map((tire) => tire.unitPrice.amount)).size === 1;

  return (
    <div className='flex min-w-0 flex-1 items-start gap-4'>
      <span className='flex min-h-10 w-4 shrink-0 items-center font-medium text-muted-foreground tabular-nums'>
        {number}
      </span>
      {/* Sized off the row, not the viewport: the card sits in a page column of any width. Narrow,
          price moves up beside the tire and EU label + quantity share a second line. */}
      <div className='@container min-w-0 flex-1'>
        <div className='grid grid-cols-[minmax(0,1fr)_auto] items-start gap-x-4 gap-y-2 @2xl:grid-cols-[minmax(0,1fr)_10rem_7rem_8rem]'>
          <div className='order-1 flex min-w-0 flex-col gap-2 @2xl:order-none'>
            {lines.map((line) => (
              <TireLine
                key={line.tire.id}
                line={line}
                axleLabel={lines.length > 1 ? getAxleLabel(line.axleType, texts) : undefined}
              />
            ))}
            {(option.isRecommended || option.isSelected || option.classification || option.priority != null) && (
              <div className='flex flex-wrap gap-2'>
                {option.classification && <TireClassificationBadge classification={option.classification} />}
                {option.isSelected && <NoddiBadge variant='success'>{texts.selectedByCustomer}</NoddiBadge>}
                {option.isRecommended && (
                  <NoddiBadge variant='outline'>
                    <NoddiIcon name='Star' size='small' />
                    {texts.recommended}
                  </NoddiBadge>
                )}
                {option.priority != null && (
                  <TirePriorityBadge priority={option.priority}>{texts.priority(option.priority)}</TirePriorityBadge>
                )}
              </div>
            )}
          </div>
          {/* A phone-width card gives the grades their own row (beside the tire count they ran under it), and drops it
              when no tire has any; wider cards keep the cell so the columns stay in place. */}
          <div className='order-4 col-span-2 flex min-w-0 flex-col gap-2 empty:hidden @md:order-3 @md:col-span-1 @md:empty:flex @2xl:order-none'>
            {lines.map((line) => (
              <TireEuLabelGrades key={line.tire.id} tire={line.tire} translations={texts} className='min-h-10' />
            ))}
          </div>
          <div className='order-3 col-start-2 flex min-h-10 flex-col justify-center gap-0.5 text-right @md:order-4 @md:col-start-auto @2xl:order-none @2xl:text-left'>
            <span>{texts.tireCount(totalQuantity)}</span>
            {frontQuantity > 0 && rearQuantity > 0 && (
              <span className='text-xs text-muted-foreground'>{texts.axleQuantities(frontQuantity, rearQuantity)}</span>
            )}
          </div>
          <div className='order-2 flex min-h-10 flex-col justify-center gap-0.5 text-right tabular-nums @2xl:order-none'>
            <span className='font-semibold'>
              {formatCurrencyAmount({ money: { amount: totalAmount, currency }, decimals: 0 })}
            </span>
            {hasOneUnitPrice && tires[0] && (
              <span className='text-xs text-muted-foreground'>
                {texts.perTire(formatCurrencyAmount({ money: tires[0].unitPrice, decimals: 0 }))}
              </span>
            )}
          </div>
        </div>
      </div>
      {actions.length > 0 && (
        <div className='flex min-h-10 shrink-0 items-center gap-2'>
          {actions.map((action) => (
            <NoddiIconButton
              key={action.label}
              iconName={action.icon}
              variant='subtle'
              aria-label={action.label}
              title={action.label}
              aria-pressed={action.active}
              className={actionButtonVariants({ active: action.active ?? false })}
              onClick={action.onClick}
              disabled={action.disabled}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const TireLine = ({ line, axleLabel }: { line: TireOptionTire; axleLabel?: string }) => {
  const { tire, imageUrl } = line;
  const details = [axleLabel, getDimensionsLoadSpeedString(tire).trim()].filter(Boolean).join(' · ');

  return (
    <div className='flex min-w-0 items-center gap-3'>
      {imageUrl ? (
        // The name beside it says what the photo shows.
        <img src={imageUrl} alt='' className='size-10 shrink-0 rounded-md bg-muted object-contain' />
      ) : (
        <span className='flex size-10 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground'>
          <NoddiIcon name='Wheel' />
        </span>
      )}
      <div className='flex min-w-0 flex-col gap-0.5'>
        <span className='truncate'>
          <span className='font-semibold'>{getTireBrandName(tire)}</span> {tire.model}
        </span>
        <span className='truncate text-xs text-muted-foreground'>{details}</span>
      </div>
    </div>
  );
};

// When front and rear get the same tire, the option shows one line, not one per axle.
const getTireLines = (tires: TireOptionTire[]) =>
  tires.filter((tire, index) => tires.findIndex((other) => other.tire.id === tire.tire.id) === index);

const sumQuantity = (tires: TireOptionTire[]) => tires.reduce((sum, tire) => sum + tire.quantity, 0);

const getAxleLabel = (axleType: CarAxleType | undefined, texts: Translations) => {
  switch (axleType) {
    case CarAxleType.FRONT:
      return texts.frontAxle;
    case CarAxleType.REAR:
      return texts.rearAxle;
    default:
      return undefined;
  }
};
